<template>
    <div>

        <div :class="[ {'disable-datepicker': noDateDefined} ]">
            <HotelDatePicker
                @check-in-changed="checkInChanged($event)"
                @check-out-changed="checkOutChanged($event)"
                @click.native="addSeeNextMonthButton"
                :format="dateFormat"
                :showYear="true"
                :i18n="dictionary"
                :startingDateValue="defaultDate(checkInDate)"
                :endingDateValue="defaultDate(checkOutDate)"
                :disabledDates="disabledDates"
                :minNights="minNights"
                type='button'
                ref='datePicker'
                class="date-picker"
            ></HotelDatePicker>
        </div>

        <!-- Undefined date checkbox -->
        <div v-if="showAnyDateCheckbox" class="date-check align-self-center">
            <div class="checkbox-wrapper">
                <input type="checkbox" class="check" autocomplete="off" :checked="noDateDefined" @change="noDateDefined = !noDateDefined">    
            </div>
            <label class="label" @click="noDateDefined = !noDateDefined">{{ $t('search.date-not-defined') }}</label>
        </div>

    </div>
</template>

<script>
import moment from 'moment';
import HotelDatePicker from 'vue-hotel-datepicker';
import { mapState, mapActions } from 'vuex';

export default {
    
    name: 'DatePicker',
    
    components: {
        HotelDatePicker
    },
    
    props: {
        disabledDates: {
            type: Array,
            default: () => []
        },
        minNights: {
            type: Number,
            default: 1
        },
        alignRight: {
            type: Boolean,
            default: false
        },
        showAnyDateCheckbox: {
            type: Boolean,
            default: false
        }
    },
    
    data() {
        return {
            
            noDateDefined: false,
            
            dateFormatEn: "YYYY/MM/DD",
            dateFormatEs: "DD/MM/YYYY",
            
            spanish: {
                night: "Noche",
                nights: "Noches",
                "day-names": ["Dom", "Lun", "Mar", "Mie", "Jue", "Vie", "SaB"],
                "check-in": "Ingreso",
                "check-out": "Salida",
                "month-names": [
                    "Enero",
                    "Febrero",
                    "Marzo",
                    "Abril",
                    "Mayo",
                    "Junio",
                    "Julio",
                    "Agosto",
                    "septiembre",
                    "Octubre",
                    "Noviembre",
                    "Diciembre"
                ]
            },
            
            english: {
                night: "Night",
                nights: "Nights",
                "day-names": ["Sun", "Mon", "Tue", "Wed", "Thur", "Fri", "Sat"],
                "check-in": "Check-in",
                "check-out": "Check-Out",
                "month-names": [
                    "January",
                    "February",
                    "March",
                    "April",
                    "May",
                    "June",
                    "July",
                    "August",
                    "September",
                    "October",
                    "November",
                    "December"
                ]
            },
        }
    },

    created() {
        // Sometimes the dates are passed as query params, sometimes as route params. TODO; unify this
        let fecha_inicio = this.$route.params.fecha_inicio || this.$route.query.fecha_inicio;
        let fecha_termino = this.$route.params.fecha_termino || this.$route.query.fecha_termino;
        // Only set store dates from route/query when they are provided
        if (fecha_inicio != null && fecha_inicio !== '') {
            this.setCheckInDate(fecha_inicio);
            this.setInitDate(fecha_inicio);
        }
        if (fecha_termino != null && fecha_termino !== '') {
            this.setCheckOutDate(fecha_termino);
            this.setEndDate(fecha_termino);
        }
    },
    mounted() {
        if (this.alignRight && typeof document !== 'undefined') {
            document.body.classList.add('datepicker-align-right');
        }
    },

    beforeDestroy() {
        if (this.alignRight && typeof document !== 'undefined') {
            document.body.classList.remove('datepicker-align-right');
        }
    },

    watch: {
        alignRight(newVal) {
            if (typeof document === 'undefined') return;
            if (newVal) document.body.classList.add('datepicker-align-right');
            else document.body.classList.remove('datepicker-align-right');
        }
    },
    
    computed: {
        
        ...mapState('search', [
            "checkInDate",
            "checkOutDate",
        ]),

        dictionary() {
            return this.$i18n.locale === "es" ? this.spanish : this.english;
        },
        
        dateFormat() {
            return (this.$i18n.locale === "es") ? this.dateFormatEs : this.dateFormatEn;
        }
        
    },
    methods: {

        ...mapActions("search", [
            "setCheckInDate",
            "setCheckOutDate",
        ]),

        // The dates could be used for a search, a simulation, or to maka a reservation
        ...mapActions("simulator", [
            'setInitDate',
            'setEndDate',
        ]),
        
        /**
         * Suelta la selección y vuelve a leer las fechas deshabilitadas.
         *
         * Lo segundo es lo importante y no se ve en el nombre: el clearSelection
         * de vue-hotel-datepicker corre parseDisabledDates() y reRender(), y esa
         * es la ÚNICA forma de que el calendario se entere de que la prop
         * `disabledDates` cambió. El componente las parsea una sola vez, en su
         * beforeMount, y no tiene watcher: si le cambiás el array después, sigue
         * pintando las de antes hasta que algo lo obligue a releerlas.
         *
         * Por eso hay que llamar a esto DESPUÉS de que la prop ya tenga el valor
         * nuevo (un $nextTick alcanza), no antes.
         */
        clearSelection() {
            const picker = this.$refs.datePicker;
            if (picker && typeof picker.clearSelection === 'function') {
                picker.clearSelection();
            }
            // El calendario arranca de estas dos al montarse (startingDateValue /
            // endingDateValue). Si quedan puestas, la selección vuelve sola.
            this.setCheckInDate(null);
            this.setCheckOutDate(null);
            this.setInitDate(null);
            this.setEndDate(null);
        },

        checkInChanged(date) {
            this.setCheckInDate(date);
            this.setInitDate(date);
        },
        
        checkOutChanged(date) {
            this.setCheckOutDate(date);
            this.setEndDate(date);
        },

        /**
         * The datepicker requires a date object, so we need to convert
         * the date string to a date object} date
         */
        defaultDate(date) {
            let tempDate =  (date === null) ? null : moment(date).utcOffset(0).toDate();
            return tempDate;
        },

        addSeeNextMonthButton() {
            if (this.$refs.datePicker) {
                let text = this.$i18n.locale === "es" ? "Siguiente mes" : "Next month";
                let seeMore = document.querySelector(".next--mobile");
                if (seeMore) {
                    seeMore.textContent = text;
                }
                // Scroll calendar bottom smooth
                let calendarContainer = document.querySelector(".datepicker__months");
                if (calendarContainer) {
                    calendarContainer.scrollBy({
                        top: calendarContainer.scrollHeight,
                        left: 0,
                        behavior: "smooth",
                    });
                }

            }
        },

       
    }
}
</script>

<style lang="scss">

.datepicker {
    z-index: 1021 !important;
    left: 0 !important;
    right: auto !important;
    transform-origin: top left;
}

.datepicker__wrapper {
    background-image: none;
    position: relative; /* asegura contexto para posicionar el popup */
}

/* Si el componente solicita alineación a la derecha, se añade la clase
   `datepicker-align-right` al body para forzar el popup hacia la derecha. */
body.datepicker-align-right .datepicker {
    left: auto !important;
    right: 0 !important;
    transform-origin: top right;
}

::v-deep .datepicker__dummy-wrapper {
    border-radius: $border-radius-new;
}

::v-deep .datepicker__input[data-qa="datepickerInput"] {
    display: flex;
    align-items: center;
    text-indent: 0;
    padding: 18px !important;
    
}
::v-deep .datepicker__input[data-qa="datepickerInput"]::before {
    content: '';
    width: 22px;
    height: 24px;
    background-image: url("~@/new-assets/icons/home-enter.svg");
    background-repeat: no-repeat;
    margin-right: 7px;
}
::v-deep .datepicker__input[data-qa="datepickerInput"]:first-child::before {
    padding: 10px !important;
}
::v-deep .datepicker__input[data-qa="datepickerInput"]:last-child::before {
    background-image: url("~@/new-assets/icons/home-exit.svg");
}
::v-deep .datepicker__clear-button {
    padding: 8px 11px;
}

::v-deep .disable-datepicker {
    pointer-events: none;
    opacity: 0.5;
}

.date-check {

    display: flex;
    margin: 0 12px;
    height: 56px;
    text-align: left;
    align-items: center;

    .checkbox-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid $grey-10;
        border-radius: 30%;
    }
    
    .check {
        width: 20px;
        height: 20px;
        border-radius: 30%;
        border: 3px solid #ffffff;
        appearance: none;
        -webkit-appearance: none;
        cursor: pointer;
    }

    .check:checked {
        background-color: $hf-primary;
    }


    .label {
        color: $grey-75;
        margin-left: 10px;
        margin-top: 6px;
        
    }
}

// Next month button
.next--mobile {
    position: fixed;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    bottom: 0;
    background: $hf-sec-pine;
    height: $see-more-button-height;
    color: #FFFFFF;
    font-size: 1.2rem;
    font-style: italic;
    z-index: 1021 !important;
}

.date-picker {
    padding-top: 6px;
    height: 50px!important
    
}

.date-picker > div {
    border-radius: $border-radius;
}

</style>